/**
 * Searchable supplement picker component.
 *
 * When the search query is empty, renders grouped <optgroup> options
 * identical to the original StockSolutionsPage dropdown. When the user
 * types, renders a flat alphabetically-sorted filtered list across all
 * categories.
 */
import { useState, useMemo } from 'react';
import type { SolutionsCatalog, Solution } from '@reefapp/shared';

interface SupplementPickerProps {
  solutions: SolutionsCatalog;
  selectedKey: string;
  onSelect(key: string): void;
}

export function SupplementPicker({
  solutions,
  selectedKey,
  onSelect,
}: SupplementPickerProps) {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const solutionEntries = Object.entries(solutions) as [string, Solution][];
    const groups: Record<string, { key: string; name: string }[]> = {};

    for (const [key, solution] of solutionEntries) {
      const category = solution.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push({ key, name: solution.name });
    }

    for (const category in groups) {
      const arr = groups[category];
      if (arr) arr.sort((a, b) => a.name.localeCompare(b.name));
    }

    return groups;
  }, [solutions]);

  const filtered = useMemo(() => {
    if (query.trim() === '') return null;

    const q = query.toLowerCase();
    const matches: { key: string; name: string }[] = [];

    for (const [key, solution] of Object.entries(solutions) as [string, Solution][]) {
      if (solution.name.toLowerCase().includes(q)) {
        matches.push({ key, name: solution.name });
      }
    }

    matches.sort((a, b) => a.name.localeCompare(b.name));
    return matches;
  }, [solutions, query]);

  const handleClear = () => {
    setQuery('');
  };

  return (
    <div>
      <div className="relative mb-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search supplements..."
          className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2 pl-3 pr-8 text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-lg leading-none"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
      <select
        id="solution-select"
        value={selectedKey}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2 px-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
      >
        <option value="">-- Choose a Supplement --</option>
        {filtered !== null
          ? filtered.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))
          : Object.entries(grouped).map(([category, solutionList]) => (
              <optgroup label={category} key={category}>
                {solutionList.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ))}
      </select>
    </div>
  );
}
